import { Button, Menu } from '@systemeio/components'
import { DotsHorizontalIcon } from '@systemeio/icons'
import type { ReactNode } from 'react'
import { twJoin } from 'tailwind-merge'

interface ActionMenuTriggerState {
  open: boolean
}

interface ActionMenuItemProps extends Omit<Menu.ActionItem, 'id' | 'onSelect'> {
  dataTestAttributes?: Record<string, string>
  onClick: () => void | Promise<void>
}

type ActionMenuItem = (ActionMenuItemProps & { id: string }) | Menu.ItemDefinition

interface ActionMenuProps {
  ariaLabel: string
  children?: ReactNode | ((state: ActionMenuTriggerState) => ReactNode)
  className?: string
  dataTestAttributes?: Record<string, string>
  isFetching?: boolean
  menuItems: Array<ActionMenuItem | false | null | undefined>
}

function ActionMenu({
  ariaLabel,
  children,
  className,
  dataTestAttributes,
  isFetching,
  menuItems,
}: ActionMenuProps) {
  const trigger =
    children == null ? (
      <Button
        {...dataTestAttributes}
        aria-label={ariaLabel}
        disabled={isFetching}
        className={twJoin(
          'data-popup-open:bg-button-ghost-gray-pressed',
          isFetching && 'cursor-wait',
        )}
        variant="ghost-gray"
        size="icon-sm"
      >
        <DotsHorizontalIcon />
      </Button>
    ) : typeof children === 'function' ? (
      ({ open }: Menu.TriggerState) => children({ open })
    ) : (
      children
    )

  return (
    <Menu
      className={className}
      disabled={isFetching}
      trigger={trigger}
      triggerProps={children == null ? undefined : { 'aria-label': ariaLabel }}
      items={menuItems.flatMap(item => {
        if (!item) {
          return []
        }

        if (item.type === 'link' || !('onClick' in item)) {
          return item
        }

        const { dataTestAttributes, onClick, ...actionItem } = item

        return {
          ...actionItem,
          dataAttributes: dataTestAttributes,
          onSelect: onClick,
        }
      })}
    />
  )
}

export { ActionMenu }
export type { ActionMenuItemProps, ActionMenuProps }
